// src/pages/AdminClienteDetail.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  Grid,
  Button,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Avatar,
  Chip,
  Stack,
  alpha,
  useTheme,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import HomeIcon from "@mui/icons-material/Home";
import PhoneIcon from "@mui/icons-material/Phone";
import EventIcon from "@mui/icons-material/Event";
import ReplayIcon from "@mui/icons-material/Replay";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ShoppingBagRoundedIcon from "@mui/icons-material/ShoppingBagRounded";
import EuroRoundedIcon from "@mui/icons-material/EuroRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import NumbersRoundedIcon from "@mui/icons-material/NumbersRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UndoIcon from "@mui/icons-material/Undo";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";

import jsPDF from "jspdf";

import {
  doc,
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  orderBy,
  limit,
} from "firebase/firestore";

import { db } from "../contexts/firebase/config";

const REENVIAR_EMAIL_URL =
  "https://us-central1-gestao-comissoes-sintanet.cloudfunctions.net/reenviarEmailPagamento";

const ENVIAR_PDF_REFERENCIA_URL =
  "https://us-central1-gestao-comissoes-sintanet.cloudfunctions.net/enviarPdfReferenciaPagamento";

const CONFIRMAR_PAGAMENTO_URL =
  "https://us-central1-gestao-comissoes-sintanet.cloudfunctions.net/confirmarPagamentoCliente";

function fmtDate(ts) {
  try {
    if (!ts) return "—";
    const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
    return isNaN(d) ? "—" : d.toLocaleDateString("pt-PT");
  } catch {
    return "—";
  }
}

function fmtDateTime(ts) {
  try {
    if (!ts) return "—";
    const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
    return isNaN(d) ? "—" : d.toLocaleString("pt-PT");
  } catch {
    return "—";
  }
}

function statusColor(status) {
  const st = (status || "").toLowerCase();

  if (st.includes("ativo") || st.includes("pago") || st.includes("conclu")) {
    return "success";
  }

  if (st.includes("expir") || st.includes("cancel")) {
    return "error";
  }

  if (st.includes("pend")) {
    return "warning";
  }

  return "default";
}

function paymentStatusColor(status) {
  const st = (status || "").toLowerCase();

  if (st.includes("pago")) return "success";
  if (st.includes("pend")) return "warning";
  if (st.includes("expir") || st.includes("cancel")) return "error";

  return "default";
}

function money(value) {
  return Number(value || 0).toLocaleString("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function limparNomeFicheiro(value) {
  return (value || "cliente")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9-_]/g, "-")
    .replace(/-+/g, "-")
    .toLowerCase();
}

function showSuccess(message) {
  enqueueSnackbar(message, { variant: "success" });
}

function showError(message) {
  enqueueSnackbar(message, { variant: "error" });
}

function showWarning(message) {
  enqueueSnackbar(message, { variant: "warning" });
}

function InfoCard({ icon, title, value, color }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.2,
        borderRadius: "22px",
        border: `1px solid ${alpha(color, 0.12)}`,
        bgcolor: alpha(color, 0.04),
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            width: 46,
            height: 46,
            borderRadius: "16px",
            bgcolor: alpha(color, 0.12),
            color,
          }}
        >
          {icon}
        </Avatar>

        <Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 900,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color,
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              mt: 0.4,
              fontSize: "1.5rem",
              fontWeight: 950,
              lineHeight: 1,
            }}
          >
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function DetailRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Avatar
        sx={{
          width: 38,
          height: 38,
          borderRadius: "14px",
          bgcolor: "rgba(37,99,235,0.08)",
          color: "#2563eb",
        }}
      >
        {icon}
      </Avatar>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "text.secondary",
          }}
        >
          {label}
        </Typography>

        <Typography sx={{ mt: 0.3, fontWeight: 800 }}>
          {value || "—"}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function AdminClienteDetail() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { clienteId } = useParams();

  const ui = {
    bg: isDark ? "#020617" : "#f6f8fc",
    card: isDark ? "rgba(15,23,42,0.88)" : "#ffffff",
    border: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
    soft: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
  };

  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compras, setCompras] = useState([]);
  const [logs, setLogs] = useState([]);

  const [reenviando, setReenviando] = useState(false);
  const [enviandoPdf, setEnviandoPdf] = useState(false);
  const [updatingPagamento, setUpdatingPagamento] = useState(false);

  useEffect(() => {
    if (!clienteId) return;

    const unsub = onSnapshot(doc(db, "clientes", clienteId), (snap) => {
      if (snap.exists()) {
        setCliente({ id: snap.id, ...snap.data() });
      } else {
        setCliente(null);
      }

      setLoading(false);
    });

    return unsub;
  }, [clienteId]);

  useEffect(() => {
    if (!clienteId) return;

    const q = query(
      collection(db, "compras"),
      where("clienteId", "==", clienteId)
    );

    const unsub = onSnapshot(q, (snap) => {
      setCompras(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return unsub;
  }, [clienteId]);

  useEffect(() => {
    if (!clienteId) return;

    const q = query(
      collection(db, "pagamentosLogs"),
      where("clienteId", "==", clienteId),
      orderBy("criadoEm", "desc"),
      limit(15)
    );

    const unsub = onSnapshot(q, (snap) => {
      setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return unsub;
  }, [clienteId]);

  async function copiar(texto, tipo = "Copiado") {
    try {
      await navigator.clipboard.writeText(String(texto || ""));
      showSuccess(`${tipo} para a área de transferência.`);
    } catch {
      showError("Erro ao copiar.");
    }
  }

  async function marcarComoPago() {
    try {
      setUpdatingPagamento(true);

      const response = await fetch(
        `${CONFIRMAR_PAGAMENTO_URL}?clienteId=${encodeURIComponent(cliente.id)}`,
        { method: "GET" }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.error || "Erro ao confirmar pagamento.");
      }

      showSuccess("Pagamento confirmado com sucesso.");
    } catch (error) {
      console.error(error);
      showError(error.message || "Erro ao confirmar pagamento.");
    } finally {
      setUpdatingPagamento(false);
    }
  }

  async function reverterPagamento() {
    try {
      setUpdatingPagamento(true);

      await updateDoc(doc(db, "clientes", cliente.id), {
        "referenciaPagamento.estado": "pendente",
        "referenciaPagamento.pagoEm": null,
        atualizadoEm: serverTimestamp(),
      });

      showWarning("Pagamento revertido.");
    } catch (error) {
      console.error(error);
      showError("Erro ao reverter pagamento.");
    } finally {
      setUpdatingPagamento(false);
    }
  }

  async function reenviarEmail() {
    try {
      setReenviando(true);

      await fetch(
        `${REENVIAR_EMAIL_URL}?clienteId=${encodeURIComponent(cliente.id)}`,
        { method: "GET", mode: "no-cors" }
      );

      showSuccess("Pedido de reenvio enviado.");
    } catch (error) {
      console.error(error);
      showError("Erro ao reenviar email.");
    } finally {
      setReenviando(false);
    }
  }

  async function enviarPdf() {
    try {
      setEnviandoPdf(true);

      const response = await fetch(
        `${ENVIAR_PDF_REFERENCIA_URL}?clienteId=${encodeURIComponent(cliente.id)}`,
        { method: "GET" }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.error || "Erro ao enviar PDF.");
      }

      showSuccess("PDF enviado.");
    } catch (error) {
      console.error(error);
      showError(error.message || "Erro ao enviar PDF.");
    } finally {
      setEnviandoPdf(false);
    }
  }

  function gerarPdfReferencia() {
    if (!cliente?.referenciaPagamento) {
      showWarning("Este cliente ainda não tem referência de pagamento.");
      return;
    }

    const ref = cliente.referenciaPagamento;
    const pdf = new jsPDF();

    pdf.setFontSize(24);
    pdf.text("Sintanet", 20, 25);

    pdf.setFontSize(15);
    pdf.text("Referência de Pagamento", 20, 40);

    pdf.setFontSize(12);
    pdf.text(`Cliente: ${cliente.nome || "—"}`, 20, 70);
    pdf.text(`Email: ${cliente.email || "—"}`, 20, 80);
    pdf.text(`Plano: ${cliente.plano || "—"}`, 20, 90);

    pdf.text(`Entidade: ${ref.entidade || "—"}`, 20, 120);
    pdf.text(`Referência: ${ref.referencia || "—"}`, 20, 130);
    pdf.text(`Valor: € ${money(ref.valor)}`, 20, 140);
    pdf.text(`Estado: ${ref.estado || "pendente"}`, 20, 150);
    pdf.text(`Validade: ${ref.validade || "—"}`, 20, 160);

    const nome = limparNomeFicheiro(cliente.nome || cliente.id);
    pdf.save(`referencia-${nome}.pdf`);

    showSuccess("PDF gerado.");
  }

  const referenciaPagamento = cliente?.referenciaPagamento;

  const pagamentoPago =
    referenciaPagamento?.estado?.toLowerCase() === "pago";

  const totalCompras = compras.length;

  const totalValor = useMemo(() => {
    return compras.reduce((acc, compra) => {
      return acc + Number(compra.valor || 0);
    }, 0);
  }, [compras]);

  if (loading) {
    return (
      <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!cliente) {
    return <Alert severity="error">Cliente não encontrado.</Alert>;
  }

  return (
    <Box sx={{ minHeight: "100%", bgcolor: ui.bg }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          borderRadius: "30px",
          mb: 3,
          background: isDark
            ? "linear-gradient(135deg,#020617,#0f172a,#1e1b4b)"
            : "linear-gradient(135deg,#0f172a,#1e3a8a,#312e81)",
          color: "#fff",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          spacing={3}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 74,
                height: 74,
                borderRadius: "24px",
                bgcolor: "rgba(255,255,255,0.14)",
                fontSize: 28,
                fontWeight: 950,
              }}
            >
              {(cliente.nome || "C").charAt(0).toUpperCase()}
            </Avatar>

            <Box>
              <Typography
                sx={{
                  fontSize: { xs: "2rem", md: "2.8rem" },
                  fontWeight: 950,
                  lineHeight: 1,
                  letterSpacing: "-0.06em",
                }}
              >
                {cliente.nome || "Cliente"}
              </Typography>

              <Typography sx={{ mt: 1, color: "rgba(255,255,255,0.72)" }}>
                Painel administrativo do cliente
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                <Chip
                  label={cliente.estado || "Sem estado"}
                  color={statusColor(cliente.estado)}
                  sx={{ fontWeight: 900 }}
                />

                {referenciaPagamento && (
                  <Chip
                    label={referenciaPagamento.estado || "pendente"}
                    color={paymentStatusColor(referenciaPagamento.estado)}
                    sx={{ fontWeight: 900 }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
            <Button
              component={RouterLink}
              to="/admin/clientes"
              startIcon={<ArrowBackIcon />}
              variant="contained"
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 900,
                bgcolor: "rgba(255,255,255,0.12)",
              }}
            >
              Voltar
            </Button>

            <Button
              startIcon={<PictureAsPdfIcon />}
              onClick={gerarPdfReferencia}
              variant="contained"
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 900,
              }}
            >
              Gerar PDF
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2.2}>
        <Grid item xs={12} md={4}>
          <InfoCard
            title="Compras"
            value={totalCompras}
            icon={<ShoppingBagRoundedIcon />}
            color={theme.palette.primary.main}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <InfoCard
            title="Valor Total"
            value={`€ ${money(totalValor)}`}
            icon={<EuroRoundedIcon />}
            color={theme.palette.success.main}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <InfoCard
            title="Plano"
            value={cliente.plano || "—"}
            icon={<WorkspacePremiumRoundedIcon />}
            color={theme.palette.warning.main}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.2} sx={{ mt: 0.2 }}>
        <Grid item xs={12} lg={5}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "28px",
              border: `1px solid ${ui.border}`,
              bgcolor: ui.card,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1.3} alignItems="center" sx={{ mb: 3 }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                  }}
                >
                  <PersonRoundedIcon />
                </Avatar>

                <Box>
                  <Typography sx={{ fontWeight: 950 }}>
                    Informações do Cliente
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Dados pessoais e administrativos
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={2.5}>
                <DetailRow icon={<EmailIcon />} label="Email" value={cliente.email} />
                <DetailRow icon={<PhoneIcon />} label="Telefone" value={cliente.telefone || cliente.contacto} />
                <DetailRow icon={<BadgeIcon />} label="NIF" value={cliente.nif} />
                <DetailRow icon={<HomeIcon />} label="Morada" value={cliente.morada} />
                <DetailRow icon={<EventIcon />} label="Data de Fim" value={fmtDate(cliente.dataFim)} />
                <DetailRow icon={<BusinessIcon />} label="Empresa" value={cliente.empresaNome} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "28px",
              border: `1px solid ${ui.border}`,
              bgcolor: ui.card,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1.3} alignItems="center" sx={{ mb: 3 }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.12),
                    color: theme.palette.success.main,
                  }}
                >
                  <CreditCardRoundedIcon />
                </Avatar>

                <Box>
                  <Typography sx={{ fontWeight: 950 }}>Pagamento</Typography>

                  <Typography variant="body2" color="text.secondary">
                    Referência multibanco e estado
                  </Typography>
                </Box>
              </Stack>

              {!referenciaPagamento ? (
                <Alert severity="warning">
                  Este cliente ainda não possui referência de pagamento.
                </Alert>
              ) : (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <DetailRow
                        icon={<AccountBalanceRoundedIcon />}
                        label="Entidade"
                        value={referenciaPagamento.entidade}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <DetailRow
                        icon={<NumbersRoundedIcon />}
                        label="Referência"
                        value={referenciaPagamento.referencia}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <DetailRow
                        icon={<PaidRoundedIcon />}
                        label="Valor"
                        value={`€ ${money(referenciaPagamento.valor)}`}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <DetailRow
                        icon={<AccessTimeRoundedIcon />}
                        label="Validade"
                        value={referenciaPagamento.validade}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      startIcon={<ContentCopyIcon />}
                      onClick={() =>
                        copiar(referenciaPagamento.referencia, "Referência copiada")
                      }
                      variant="outlined"
                      sx={{
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 900,
                      }}
                    >
                      Copiar referência
                    </Button>

                    <Button
                      startIcon={<ReplayIcon />}
                      onClick={reenviarEmail}
                      disabled={reenviando}
                      variant="outlined"
                      sx={{
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 900,
                      }}
                    >
                      {reenviando ? "A reenviar..." : "Reenviar email"}
                    </Button>

                    <Button
                      startIcon={<PictureAsPdfIcon />}
                      onClick={enviarPdf}
                      disabled={enviandoPdf}
                      variant="outlined"
                      sx={{
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 900,
                      }}
                    >
                      {enviandoPdf ? "A enviar..." : "Enviar PDF"}
                    </Button>

                    {!pagamentoPago ? (
                      <Button
                        startIcon={<CheckCircleIcon />}
                        onClick={marcarComoPago}
                        disabled={updatingPagamento}
                        variant="contained"
                        color="success"
                        sx={{
                          borderRadius: 999,
                          textTransform: "none",
                          fontWeight: 900,
                        }}
                      >
                        Confirmar pagamento
                      </Button>
                    ) : (
                      <Button
                        startIcon={<UndoIcon />}
                        onClick={reverterPagamento}
                        disabled={updatingPagamento}
                        variant="contained"
                        color="error"
                        sx={{
                          borderRadius: 999,
                          textTransform: "none",
                          fontWeight: 900,
                        }}
                      >
                        Reverter pagamento
                      </Button>
                    )}
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "28px",
              border: `1px solid ${ui.border}`,
              bgcolor: ui.card,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1.3} alignItems="center" sx={{ mb: 3 }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.warning.main, 0.12),
                    color: theme.palette.warning.main,
                  }}
                >
                  <ShoppingBagRoundedIcon />
                </Avatar>

                <Box>
                  <Typography sx={{ fontWeight: 950 }}>Compras</Typography>

                  <Typography variant="body2" color="text.secondary">
                    Histórico de compras do cliente
                  </Typography>
                </Box>
              </Stack>

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: "20px",
                  border: `1px solid ${ui.border}`,
                  overflow: "hidden",
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Produto</strong></TableCell>
                      <TableCell><strong>Valor</strong></TableCell>
                      <TableCell><strong>Estado</strong></TableCell>
                      <TableCell><strong>Data</strong></TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {compras.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                            Nenhuma compra encontrada.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      compras.map((compra) => (
                        <TableRow key={compra.id}>
                          <TableCell>
                            {compra.nome || compra.produto || "—"}
                          </TableCell>

                          <TableCell>€ {money(compra.valor)}</TableCell>

                          <TableCell>
                            <Chip
                              label={compra.estado || compra.status || "—"}
                              color={statusColor(compra.estado || compra.status)}
                              size="small"
                              sx={{ fontWeight: 900 }}
                            />
                          </TableCell>

                          <TableCell>
                            {fmtDate(compra.criadoEm || compra.dataCompra)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "28px",
              border: `1px solid ${ui.border}`,
              bgcolor: ui.card,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1.3} alignItems="center" sx={{ mb: 3 }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.12),
                    color: theme.palette.info.main,
                  }}
                >
                  <TimelineRoundedIcon />
                </Avatar>

                <Box>
                  <Typography sx={{ fontWeight: 950 }}>
                    Atividade Recente
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Últimas ações relacionadas com este cliente
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={1.4}>
                {logs.length === 0 ? (
                  <Alert severity="info">
                    Ainda não existem logs para este cliente.
                  </Alert>
                ) : (
                  logs.map((log) => (
                    <Paper
                      key={log.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: "18px",
                        border: `1px solid ${ui.border}`,
                        bgcolor: ui.soft,
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        justifyContent="space-between"
                        spacing={1.5}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 900 }}>
                            {log.acao || "Atividade"}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.4 }}
                          >
                            {log.detalhe || "Sem detalhe."}
                          </Typography>
                        </Box>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ flexShrink: 0 }}
                        >
                          {fmtDateTime(log.criadoEm)}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}